import { get } from 'svelte/store';

import {
	get_z_wave_component_from_category,
	getZWaveChannelIndex,
	real_id,
} from 'src/helpers/interface_helpers';
import { getXzComponentId, xzRequest } from 'src/services/xz_http_reqs';
import { devices_settings, devices_status } from 'src/stores';
import type { Device } from 'src/types/Device';

const logWaveActuator = (stage: string, data: Record<string, unknown> = {}) => {
	console.log(`[zwave][actuator-config] ${stage}`, data);
};

/** RPC / store prefix without trailing digits (`switch:` / `light:`). */
export const getWaveActuatorPrefix = (device: Device): string => {
	const prefix = get_z_wave_component_from_category(device);
	return prefix === 'cover:' || prefix === 'input:' ? 'switch:' : prefix;
};

/** Gen2 method root: `Switch` or `Light`. */
export const getWaveActuatorRpcName = (device: Device): 'Switch' | 'Light' => {
	return getWaveActuatorPrefix(device) === 'light:' ? 'Light' : 'Switch';
};

export const getWaveActuatorComponentKey = (device: Device): string => {
	return `${getWaveActuatorPrefix(device)}${getWaveActuatorComponentId(device)}`;
};

export const getWaveActuatorComponentId = (device: Device): number => {
	const prefix = getWaveActuatorPrefix(device);
	const channelIndex = getZWaveChannelIndex(device);
	const preferredId = 100 + channelIndex;
	const preferredKey = `${prefix}${preferredId}`;
	const settingsPayload = get(devices_settings)?.[real_id(device.id)] || {};
	const statusPayload = devices_status.getByRealId(device.id) || {};

	if (settingsPayload[preferredKey] || statusPayload[preferredKey]) {
		return preferredId;
	}

	const matchedByChannel = Object.entries(settingsPayload).find(
		([key, value]) =>
			key.startsWith(prefix) && Number((value as { _chnm?: number })?._chnm) === channelIndex,
	);
	if (matchedByChannel) {
		const componentId = Number(matchedByChannel[0].split(':')[1]);
		if (Number.isFinite(componentId)) {
			return componentId;
		}
	}

	const fromSettings = Object.keys(settingsPayload).find((key) => key.startsWith(prefix));
	const fromStatus = Object.keys(statusPayload).find((key) => key.startsWith(prefix));
	const existingKey = fromSettings || fromStatus;
	if (existingKey) {
		const componentId = Number(existingKey.split(':')[1]);
		if (Number.isFinite(componentId)) {
			return componentId;
		}
	}
	return getXzComponentId(device);
};

export const mergeWaveActuatorConfig = (
	deviceId: string,
	prefix: string,
	componentId: number,
	config: Record<string, unknown>,
): void => {
	const id = real_id(deviceId);
	const existing = get(devices_settings)?.[id] || {};
	const key = `${prefix}${componentId}`;
	devices_settings.update_devices_settings(id, {
		...existing,
		[key]: {
			...(existing[key] || {}),
			id: componentId,
			...config,
		},
	});
};

export const readWaveActuatorConfig = (device: Device): Record<string, unknown> => {
	const id = real_id(device.id);
	const componentId = getWaveActuatorComponentId(device);
	const prefix = getWaveActuatorPrefix(device);
	return get(devices_settings)?.[id]?.[`${prefix}${componentId}`] || {};
};

/**
 * Switch|Light.GetConfig via XZ and merge into devices_settings[switch|light:id].
 */
export const zwaveActuatorGetConfig = async (device: Device) => {
	const componentId = getWaveActuatorComponentId(device);
	const prefix = getWaveActuatorPrefix(device);
	const rpcName = getWaveActuatorRpcName(device);
	const response = await xzRequest(device, `${rpcName}.GetConfig`, { id: componentId });

	if (response?.isok) {
		const config =
			(response.data && typeof response.data === 'object' ? response.data : null) ||
			(response.result && typeof response.result === 'object' ? response.result : null) ||
			{};
		mergeWaveActuatorConfig(device.id, prefix, componentId, config as Record<string, unknown>);
		logWaveActuator('get_config_ok', { deviceId: device.id, componentId, rpcName });
	} else {
		logWaveActuator('get_config_failed', { deviceId: device.id, componentId, rpcName, response });
	}

	return response;
};

/**
 * Switch|Light.SetConfig via XZ and merge successful config patch into store.
 */
export const zwaveActuatorSetConfig = async (device: Device, config: Record<string, unknown>) => {
	const componentId = getWaveActuatorComponentId(device);
	const prefix = getWaveActuatorPrefix(device);
	const rpcName = getWaveActuatorRpcName(device);
	const response = await xzRequest(device, `${rpcName}.SetConfig`, {
		id: componentId,
		config,
	});

	if (response?.isok) {
		mergeWaveActuatorConfig(device.id, prefix, componentId, config);
	}

	return response;
};
